import { axiosInstance } from './index'

export interface UploadFacilitiesInspectionRequest {
    file: File
    email: string
}

export interface UploadFacilitiesInspectionResponse {
    success: boolean
    message: string
    job_id?: string
    estimated_completion_time?: string
}

/**
 * Uploads a facilities inspection sheet file with email notification
 * @param request - The upload request containing file and email
 * @returns Promise with upload response
 */
export const uploadFacilitiesInspectionSheet = async (
    request: UploadFacilitiesInspectionRequest,
): Promise<UploadFacilitiesInspectionResponse> => {
    try {
        const formData = new FormData()
        formData.append('file', request.file)
        formData.append('email', request.email)

        const response = await axiosInstance.post(
            'inspection/upload_facilities_spreadsheet/',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        )

        return {
            success: true,
            message:
                'Facilities inspection sheet uploaded successfully. You will receive an email with the results shortly.',
            job_id: response.data?.job_id,
            estimated_completion_time: response.data?.estimated_completion_time,
        }
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Error uploading facilities inspection sheet'

        throw new Error(errorMessage)
    }
}

/**
 * Validates file type for facilities inspection upload
 * @param file - The file to validate
 * @returns boolean indicating if file is valid
 */
export const validateFacilitiesInspectionFile = (file: File): boolean => {
    const allowedTypes = [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
    ]

    const allowedExtensions = ['.csv', '.xlsx', '.xls']

    // Check MIME type
    if (allowedTypes.includes(file.type)) {
        return true
    }

    // Check file extension as fallback
    const fileName = file.name.toLowerCase()
    return allowedExtensions.some((ext) => fileName.endsWith(ext))
}

/**
 * Validates file size for facilities inspection upload (max 10MB)
 * @param file - The file to validate
 * @returns boolean indicating if file size is valid
 */
export const validateFacilitiesInspectionFileSize = (file: File): boolean => {
    const maxSize = 10 * 1024 * 1024 // 10MB
    return file.size <= maxSize
}

/**
 * Validates email format
 * @param email - The email to validate
 * @returns boolean indicating if email is valid
 */
export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
}

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
